(() => {
  const HOME_SECTION_SELECTOR = '[data-tf-home-section]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initializedSections = new WeakMap();
  const revealElements = new Set();
  let revealObserver = null;

  function createRevealObserver() {
    if (reducedMotion.matches || !('IntersectionObserver' in window)) return null;

    return new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
          revealElements.delete(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.12,
      }
    );
  }

  function observeReveals(root) {
    const elements = root.matches?.('[data-tf-reveal]')
      ? [root]
      : Array.from(root.querySelectorAll?.('[data-tf-reveal]') || []);

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    revealObserver ||= createRevealObserver();
    elements.forEach((element) => {
      if (element.classList.contains('is-visible') || revealElements.has(element)) return;
      revealElements.add(element);
      revealObserver?.observe(element);
    });
  }

  function initializeHero(section, signal) {
    const feed = section.matches?.('[data-tf-hero]') ? section : section.querySelector?.('[data-tf-hero]');
    const video = feed?.querySelector('.tf-feed__base video');
    if (!video) return;

    const toggle = feed.querySelector('[data-tf-hero-toggle]');
    const toggleLabel = toggle?.querySelector('[data-tf-hero-toggle-label]');

    function syncToggle() {
      if (!toggle) return;
      const isPlaying = !video.paused && !video.ended;
      const label = isPlaying ? toggle.dataset.labelPause : toggle.dataset.labelPlay;
      toggle.dataset.state = isPlaying ? 'playing' : 'paused';
      toggle.setAttribute('aria-label', label || '');
      if (toggleLabel) toggleLabel.textContent = label || '';
    }

    video.addEventListener('play', syncToggle, { signal });
    video.addEventListener('pause', syncToggle, { signal });
    toggle?.addEventListener('click', () => {
      if (video.paused) {
        video.play().catch(syncToggle);
      } else {
        video.pause();
      }
    }, { signal });

    if (reducedMotion.matches) {
      video.pause();
      syncToggle();
      return;
    }

    video.play().catch(syncToggle);
    syncToggle();
  }

  function initializeInstallation(section, signal) {
    const installation = section.matches?.('[data-tf-installation]')
      ? section
      : section.querySelector?.('[data-tf-installation]');
    if (!installation) return;

    const items = Array.from(installation.querySelectorAll('[data-tf-installation-item]'));
    const layers = Array.from(installation.querySelectorAll('[data-tf-installation-layer]'));
    const narrativeLayout = window.matchMedia('(min-width: 960px) and (min-height: 640px)');
    if (items.length === 0 || items.length !== layers.length) return;

    let activeIndex = Math.max(
      0,
      items.findIndex((item) => item.classList.contains('is-active'))
    );
    let stepObserver = null;

    function syncNarrativeMode() {
      const isNarrative = narrativeLayout.matches;
      installation.classList.toggle('is-static', !isNarrative);
      items.forEach((item) => {
        const button = item.querySelector('[data-tf-installation-trigger]');
        if (!button) return;
        if (isNarrative) {
          button.removeAttribute('role');
          button.removeAttribute('aria-level');
          button.removeAttribute('tabindex');
        } else {
          button.setAttribute('role', 'heading');
          button.setAttribute('aria-level', '3');
          button.setAttribute('tabindex', '-1');
        }
      });
    }

    function activateStep(nextIndex) {
      activeIndex = Math.max(0, Math.min(nextIndex, items.length - 1));
      items.forEach((item, index) => {
        const button = item.querySelector('[data-tf-installation-trigger]');
        const isActive = index === activeIndex;
        item.classList.toggle('is-active', isActive);
        button?.toggleAttribute('aria-current', isActive);
        if (isActive) button?.setAttribute('aria-current', 'step');

        const layer = layers[index];
        layer?.classList.toggle('is-active', isActive);
        layer?.setAttribute('aria-hidden', String(!isActive));
        layer?.toggleAttribute('inert', !isActive);
      });
    }

    items.forEach((item, index) => {
      const button = item.querySelector('[data-tf-installation-trigger]');
      if (!button) return;

      button.addEventListener('click', () => {
        if (narrativeLayout.matches) activateStep(index);
      }, { signal });
      button.addEventListener('focus', () => {
        if (narrativeLayout.matches) activateStep(index);
      }, { signal });

      button.addEventListener(
        'keydown',
        (event) => {
          if (!narrativeLayout.matches || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();

          let nextIndex = index;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = items.length - 1;
          if (event.key === 'ArrowDown') nextIndex = (index + 1) % items.length;
          if (event.key === 'ArrowUp') nextIndex = (index - 1 + items.length) % items.length;

          activateStep(nextIndex);
          items[nextIndex]?.querySelector('[data-tf-installation-trigger]')?.focus();
        },
        { signal }
      );
    });

    function connectStepObserver() {
      stepObserver?.disconnect();
      stepObserver = null;

      if (!narrativeLayout.matches || !('IntersectionObserver' in window)) return;
      stepObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          if (visible.length === 0) return;
          const nextIndex = items.indexOf(visible[0].target);
          if (nextIndex >= 0) activateStep(nextIndex);
        },
        { rootMargin: '-34% 0px -44% 0px', threshold: [0, 0.2, 0.5, 0.8] }
      );
      items.forEach((item) => stepObserver.observe(item));
    }

    installation.classList.add('is-enhanced');
    narrativeLayout.addEventListener('change', () => {
      syncNarrativeMode();
      connectStepObserver();
    }, { signal });
    signal.addEventListener('abort', () => stepObserver?.disconnect(), { once: true });
    syncNarrativeMode();
    activateStep(activeIndex);
    connectStepObserver();
  }

  function initializeTechnologyTabs(section, signal) {
    const technology = section.matches?.('[data-tf-technologies]')
      ? section
      : section.querySelector?.('[data-tf-technologies]');
    if (!technology) return;

    const tabList = technology.querySelector('[data-tf-tablist]');
    const tabs = Array.from(technology.querySelectorAll('[data-tf-tab]'));
    const summaries = Array.from(technology.querySelectorAll('[data-tf-tabsummary]'));
    const panels = Array.from(technology.querySelectorAll('[data-tf-tabpanel]'));
    const hasPanels = panels.length === tabs.length;
    const horizontalTabs = window.matchMedia('(max-width: 959px), (max-height: 639px)');
    if (!tabList || tabs.length === 0 || summaries.length !== tabs.length || (panels.length > 0 && !hasPanels)) return;

    technology.classList.add('is-enhanced');
    tabList.setAttribute('role', 'tablist');

    function syncTabOrientation() {
      tabList.setAttribute('aria-orientation', horizontalTabs.matches ? 'horizontal' : 'vertical');
    }

    horizontalTabs.addEventListener('change', syncTabOrientation, { signal });
    syncTabOrientation();

    function activateTab(nextIndex, moveFocus = false) {
      tabs.forEach((tab, index) => {
        const isActive = index === nextIndex;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        tab.classList.toggle('is-active', isActive);

        summaries[index]?.classList.toggle('is-active', isActive);
        summaries[index]?.setAttribute('aria-hidden', String(!isActive));
        summaries[index]?.toggleAttribute('inert', !isActive);
        if (!hasPanels) {
          summaries[index]?.setAttribute('role', 'tabpanel');
          summaries[index]?.setAttribute('tabindex', '0');
        }

        panels[index]?.classList.toggle('is-active', isActive);
        panels[index]?.setAttribute('aria-hidden', String(!isActive));
        panels[index]?.toggleAttribute('inert', !isActive);
        panels[index]?.setAttribute('role', 'tabpanel');
        panels[index]?.setAttribute('tabindex', '0');
        if (!isActive) panels[index]?.querySelectorAll('video').forEach((video) => video.pause());
      });

      if (moveFocus) tabs[nextIndex]?.focus();
    }

    tabs.forEach((tab, index) => {
      const panel = panels[index];
      const target = panel || summaries[index];
      if (!tab.id) tab.id = `tf-home-tab-${index}`;
      if (panel && !panel.id) panel.id = `tf-home-panel-${index}`;
      if (target) {
        tab.setAttribute('aria-controls', target.id);
        target.setAttribute('aria-labelledby', tab.id);
      }

      tab.addEventListener('click', () => activateTab(index), { signal });
      tab.addEventListener(
        'keydown',
        (event) => {
          const directionalKeys = horizontalTabs.matches
            ? ['ArrowLeft', 'ArrowRight']
            : ['ArrowUp', 'ArrowDown'];
          if (![...directionalKeys, 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();

          let nextIndex = index;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          if (event.key === directionalKeys[1]) nextIndex = (index + 1) % tabs.length;
          if (event.key === directionalKeys[0]) nextIndex = (index - 1 + tabs.length) % tabs.length;
          activateTab(nextIndex, true);
        },
        { signal }
      );
    });

    technology.querySelectorAll('[data-tf-compare-range]').forEach((range) => {
      const comparison = range.closest('[data-tf-compare]');
      if (!comparison) return;
      const updateComparison = () => comparison.style.setProperty('--tf-compare', `${range.value}%`);
      range.addEventListener('input', updateComparison, { signal });
      updateComparison();
    });

    activateTab(0);
  }

  function initializeSection(section) {
    initializedSections.get(section)?.abort();

    const controller = new AbortController();
    initializedSections.set(section, controller);
    initializeHero(section, controller.signal);
    initializeInstallation(section, controller.signal);
    initializeTechnologyTabs(section, controller.signal);
    observeReveals(section);
  }

  function initializeWithin(root = document) {
    const sections = root.matches?.(HOME_SECTION_SELECTOR)
      ? [root]
      : Array.from(root.querySelectorAll?.(HOME_SECTION_SELECTOR) || []);
    sections.forEach(initializeSection);
  }

  function unloadWithin(root) {
    const sections = root.matches?.(HOME_SECTION_SELECTOR)
      ? [root]
      : Array.from(root.querySelectorAll?.(HOME_SECTION_SELECTOR) || []);

    sections.forEach((section) => {
      initializedSections.get(section)?.abort();
      initializedSections.delete(section);
      section.querySelectorAll('[data-tf-reveal]').forEach((element) => {
        revealObserver?.unobserve(element);
        revealElements.delete(element);
      });
    });
  }

  function handleMotionPreference() {
    document.querySelectorAll('[data-tf-hero] video').forEach((video) => {
      if (reducedMotion.matches) {
        video.pause();
      } else {
        video.play().catch(() => {});
      }
    });

    if (!reducedMotion.matches) {
      revealObserver ||= createRevealObserver();
      revealElements.forEach((element) => revealObserver?.observe(element));
      return;
    }

    revealObserver?.disconnect();
    revealObserver = null;
    revealElements.forEach((element) => element.classList.add('is-visible'));
    revealElements.clear();
  }

  function handleBlockSelection(event) {
    const technologyPanel = event.target.closest?.('[data-tf-tabpanel]');
    if (technologyPanel) {
      const technology = technologyPanel.closest('[data-tf-technologies]');
      const panels = Array.from(technology?.querySelectorAll('[data-tf-tabpanel]') || []);
      const tabs = Array.from(technology?.querySelectorAll('[data-tf-tab]') || []);
      tabs[panels.indexOf(technologyPanel)]?.click();
      return;
    }

    const installationItem = event.target.closest?.('[data-tf-installation-item]');
    const installationTrigger = installationItem?.querySelector('[data-tf-installation-trigger]');
    if (!installationTrigger) return;
    installationTrigger.click();
  }

  function start() {
    if (!document.body.classList.contains('template-index')) return;
    initializeWithin(document);
    document.body.classList.add('tf-home-motion-ready');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => document.body.classList.add('tf-home-loaded'));
    });
  }

  reducedMotion.addEventListener('change', handleMotionPreference);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => unloadWithin(event.target));
  document.addEventListener('shopify:block:select', handleBlockSelection);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
